// ============================================================================
// PROJECT DETAIL PAGE
// Sidebar layout with sessions tree + project tabs (Knowledge Base, Outputs, File Browser, Access)
// ============================================================================

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation, Outlet, useOutlet } from 'react-router-dom';
import { CalendarClock, Crown, ChevronDown, ChevronRight, Plus, Book, Download, FolderOpen, Shield, MoreVertical, Archive, Trash2, LayoutDashboard, ClipboardList, ListChecks, Hammer, Search, Lightbulb, Share2, CalendarRange, DollarSign, LogOut, HelpCircle, FileText, Bot, Pencil, Check, X, ShieldCheck, BarChart2, Map, ClipboardCheck, BookOpen, MessageSquare, ShieldAlert, Flag, Milestone, TrendingUp, Zap, Users, GraduationCap, Rocket, Library, Table2 } from 'lucide-react';
import { useProject, useUpdateProject, useDeleteProject, useProjectSessions, useDeleteProjectContent, useDeleteSessionContent, useRenameSession, useProjectDeletionSchedules, downloadDeletionCertificate, DeletionSchedule, DeletionTargetType, BreakGlassContext } from '../hooks/useProjects';
import { ScheduleDeletionModal, formatDeleteOn, daysUntilDeleteOn } from '../components/common/ScheduleDeletionModal';
import { BreakGlassToggle } from '../components/break-glass/BreakGlassToggle';
import { BreakGlassModal } from '../components/common/BreakGlassModal';
import { useBreakGlass, useIsSystemAdmin, useOpenProjectSession } from '../contexts/BreakGlassContext';
import { useAuth } from '../contexts/AuthContext';
import { useHelp } from '../components/help/HelpContext';
import { useTrainingMode } from '../training/TrainingModeContext';
import { TrainingGuidePanel } from '../training/TrainingGuidePanel';
import { KnowledgeBaseTab } from '../components/KnowledgeBaseTab';
import { OutputsTab } from '../components/outputs/OutputsTab';
import { S3BrowserTab } from '../components/project/S3BrowserTab';
import { AccessManagementTab } from '../components/project/AccessManagementTab';
import { ConfirmModal } from '../components/common/ConfirmModal';

import { DeletionProgressModal } from '../components/common/DeletionProgressModal';
import { OverviewTab } from '../components/project/OverviewTab';
import { RequirementsTab } from '../components/project/RequirementsTab';
import { ProjectTasksTab } from '../components/project/ProjectTasksTab';
import { CreateSessionTab } from '../components/project/CreateSessionTab';
import BuildWithAI from './BuildWithAI';
import { CustomerResearchTab } from '../components/project/CustomerResearchTab';
import { IdeationTab } from '../components/project/IdeationTab';
import { ProjectGraphTab } from '../components/project/ProjectGraphTab';
import { EngagementBuilderTab } from '../components/project/engagement/EngagementBuilderTab';
import { CostsTab } from '../components/project/CostsTab';
import { ProjectAvatarsTab } from '../components/project/ProjectAvatarsTab';
import { McpServerTab } from '../components/project/McpServerTab';
import { SessionHubTab } from '../components/project/SessionHubTab';
import { DemoTemplatesTab } from '../components/project/DemoTemplatesTab';
import { StakeholdersTab } from '../components/project/StakeholdersTab';
import { MilestonesTab } from '../components/project/MilestonesTab';
import { TrainingReviewTab } from '../components/project/TrainingReviewTab';
import { SessionsTab } from '../components/project/SessionsTab';

type SidebarView = 'dashboard' | 'sessions' | 'engagement' | 'research' | 'project-graph' | 'ideation' | 'requirements' | 'milestones' | 'stakeholders' | 'tasks' | 'build' | 'knowledge-base' | 'outputs' | 'file-browser' | 'access' | 'costs' | 'new-session' | 'avatars' | 'mcp-server' | 'training-review' | 'demo-templates' | 'session-hub';

type SidebarItem = { key: SidebarView; label: string; icon: React.ElementType };

const getSessionStatusDotClass = (session: any): string => {
  if (session.contentClearedAt) return 'bg-red-500';
  if (session.status === 'active') return 'bg-green-500';
  if (session.status === 'completed') return 'bg-slate-400';
  return 'bg-amber-400';
};

const topItems: SidebarItem[] = [
  { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { key: 'sessions', label: 'Sessions', icon: Table2 },
];

const prepItems: SidebarItem[] = [
  { key: 'engagement', label: 'Engagement Planner', icon: CalendarRange },
  { key: 'research', label: 'Customer Research', icon: Search },
  { key: 'project-graph', label: 'Project Graph', icon: Share2 },
  { key: 'knowledge-base', label: 'Knowledge Base', icon: Book },
  { key: 'avatars', label: 'Project Avatars', icon: Bot },
];

const ideateItems: SidebarItem[] = [
  { key: 'ideation', label: 'Ideation', icon: Lightbulb },
];

const deliveryItems: SidebarItem[] = [
  { key: 'requirements', label: 'Requirements', icon: ClipboardList },
  { key: 'milestones', label: 'Milestones', icon: Milestone },
  { key: 'stakeholders', label: 'Stakeholders', icon: Users },
  { key: 'tasks', label: 'Tasks', icon: ListChecks },
  { key: 'build', label: 'Build with AI', icon: Hammer },
];

const manageItems: SidebarItem[] = [
  { key: 'outputs', label: 'Outputs', icon: Download },
  { key: 'file-browser', label: 'File Browser', icon: FolderOpen },
  { key: 'costs', label: 'LLM Costs', icon: DollarSign },
  { key: 'access', label: 'Access Control', icon: Shield },
];

const staticSidebarItems = [...topItems, ...prepItems, ...ideateItems, ...deliveryItems, ...manageItems];

const ProjectDetail: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { user, accessToken, can, canAny, logout } = useAuth();
  const isSystemAdmin = useIsSystemAdmin();
  const { openHelp } = useHelp();
  const { isActive: isTrainingActive, startTraining, stopTraining } = useTrainingMode();
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) { // NOSONAR
      console.error('Logout failed:', error);
    }
  };

  const hasReachFeature = can('Modules::UseReach');
  const hasBidFeature = can('Modules::UseBid');
  const hasTabletopFeature = can('Modules::UseTabletop');
  const hasMcpServer = can('Integrations::UseProjectMcp');
  const hasSessionHub = can('SessionHub::Manage');
  const hasDemoTemplates = canAny('Features::DemonstratorTemplates', 'Templates::ManageDemonstrators');

  const toggleReachEnabled = () => {
    const next = !(project as any)?.reachEnabled;
    updateProjectMutation.mutate(
      { reachEnabled: next } as any,
      { onSuccess: () => showNotification('success', next ? 'REACH enabled for this project' : 'REACH disabled for this project') }
    );
  };

  const toggleBidEnabled = () => {
    const next = !(project as any)?.bidEnabled;
    updateProjectMutation.mutate(
      { bidEnabled: next } as any,
      { onSuccess: () => showNotification('success', next ? 'Bid Response enabled for this project' : 'Bid Response disabled for this project') }
    );
  };

  const toggleTabletopEnabled = () => {
    const next = !(project as any)?.tabletopEnabled;
    updateProjectMutation.mutate(
      { tabletopEnabled: next } as any,
      { onSuccess: () => showNotification('success', next ? 'Cyber Tabletop Exercise enabled for this project' : 'Cyber Tabletop Exercise disabled for this project') }
    );
  };
  const mcpServerItem: SidebarItem = { key: 'mcp-server', label: 'MCP Server', icon: Zap };
  const sessionHubItem: SidebarItem = { key: 'session-hub', label: 'Session Hub', icon: Library };
  const demoTemplatesItem: SidebarItem = { key: 'demo-templates', label: 'Demonstrators', icon: Rocket };
  const trainingReviewItem: SidebarItem = { key: 'training-review', label: 'Training Review', icon: GraduationCap };
  const sidebarItems = useMemo(() => [
    ...staticSidebarItems,
    ...(hasMcpServer ? [mcpServerItem] : []),
    ...(hasDemoTemplates ? [demoTemplatesItem] : []),
    ...(hasSessionHub ? [sessionHubItem] : []),
    // Included unconditionally so ?tab=training-review resolves and the header
    // title looks it up. Visibility is gated on trainingSandboxEnabled where the
    // sidebar renders, which is below the project query.
    trainingReviewItem,
  ], [hasMcpServer, hasDemoTemplates, hasSessionHub]);

  const outlet = useOutlet();

  const activateView = (key: SidebarView) => {
    setActiveView(key);
    if (outlet) navigate(`/projects/${projectId}`);
  };

  const tabFromUrl = searchParams.get('tab') as SidebarView | null;
  const [activeView, setActiveView] = useState<SidebarView>(
    tabFromUrl && sidebarItems.some(i => i.key === tabFromUrl) ? tabFromUrl : 'dashboard'
  );
  const [sessionsExpanded, setSessionsExpanded] = useState(true);
  const [prepExpanded, setPrepExpanded] = useState(true);
  const [ideateExpanded, setIdeateExpanded] = useState(true);
  const [deliveryExpanded, setDeliveryExpanded] = useState(true);
  const [manageExpanded, setManageExpanded] = useState(true);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [deleteProjectFlowOpen, setDeleteProjectFlowOpen] = useState(false);
  const [clearProjectContentOpen, setClearProjectContentOpen] = useState(false);
  const [breakGlassModalTarget, setBreakGlassModalTarget] = useState<'delete' | 'clear' | 'schedule' | null>(null);
  const [clearSessionContentId, setClearSessionContentId] = useState<string | null>(null);
  const [auditSessionId, setAuditSessionId] = useState<string | null>(null);
  const [scheduleTarget, setScheduleTarget] = useState<{ type: DeletionTargetType; id: string; name: string; breakGlass?: BreakGlassContext } | null>(null);
  const [sessionMenuOpenId, setSessionMenuOpenId] = useState<string | null>(null);
  const [renamingSessionId, setRenamingSessionId] = useState<string | null>(null);
  const [renameSessionValue, setRenameSessionValue] = useState('');
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [renamingProject, setRenamingProject] = useState(false);
  const [renameValue, setRenameValue] = useState('');

  const { data: project, isLoading, error } = useProject(projectId!);
  const breakGlassScope = useMemo(() => (projectId ? { type: 'project' as const, id: projectId } : null), [projectId]);
  const { active: inBreakGlass, grant: breakGlassGrant } = useBreakGlass(breakGlassScope);
  // An active project grant (from the header toggle or the access prompt) covers the
  // owner-only actions below, so they confirm as normal instead of asking again
  const grantCtx: BreakGlassContext | undefined = breakGlassGrant
    ? { category: breakGlassGrant.category, justification: breakGlassGrant.justification }
    : undefined;
  const hasReach = hasReachFeature && !!(project as any)?.reachEnabled;
  const hasBid = hasBidFeature && !!(project as any)?.bidEnabled;
  const hasTabletop = hasTabletopFeature && !!(project as any)?.tabletopEnabled;
  const isTrainingSandbox = !!(project as any)?.trainingSandboxEnabled;
  const canSignOffTraining = can('Facilitation::SignOffTraining');
  const { data: sessions } = useProjectSessions(projectId!);
  const { data: deletionSchedules } = useProjectDeletionSchedules(projectId);
  const projectDeletion = deletionSchedules?.project;
  const projectCertificate = deletionSchedules?.certificate;
  const downloadCertificate = (type: DeletionTargetType, id: string) =>
    downloadDeletionCertificate(type, id).catch((err: any) => showNotification('error', `Certificate download failed: ${err.message}`));
  const openSession = useOpenProjectSession(projectId);
  const updateProjectMutation = useUpdateProject(projectId!);
  const deleteProjectMutation = useDeleteProject();
  const deleteProjectContentMutation = useDeleteProjectContent(projectId!);
  const deleteSessionContentMutation = useDeleteSessionContent(clearSessionContentId || '');
  const renameSessionMutation = useRenameSession(projectId!);

  useEffect(() => {
    if (!outlet) {
      setSearchParams({ tab: activeView }, { replace: true });
    }
  }, [activeView, setSearchParams, outlet]);

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const startRename = () => {
    if (inBreakGlass) return;
    setRenameValue(project!.name);
    setRenamingProject(true);
  };

  const submitRename = () => {
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed === project!.name) { setRenamingProject(false); return; }
    updateProjectMutation.mutate(
      { name: trimmed },
      {
        onSuccess: () => { showNotification('success', 'Project renamed'); setRenamingProject(false); },
        onError: (err: any) => { showNotification('error', `Rename failed: ${err.message}`); setRenamingProject(false); }
      }
    );
  };

  const startSessionRename = (sid: string, currentName: string) => {
    setRenameSessionValue(currentName);
    setRenamingSessionId(sid);
    setSessionMenuOpenId(null);
  };

  const submitSessionRename = (sid: string, currentName: string) => {
    const trimmed = renameSessionValue.trim();
    if (!trimmed || trimmed === currentName) { setRenamingSessionId(null); return; }
    renameSessionMutation.mutate(
      { sessionId: sid, customerName: trimmed },
      {
        onSuccess: () => { showNotification('success', 'Session renamed'); setRenamingSessionId(null); },
        onError: (err: any) => { showNotification('error', `Rename failed: ${err.message}`); setRenamingSessionId(null); }
      }
    );
  };

  const confirmArchive = () => {
    updateProjectMutation.mutate(
      { status: 'archived' },
      {
        onSuccess: () => { showNotification('success', 'Project archived'); setArchiveConfirmOpen(false); },
        onError: (err: any) => { showNotification('error', `Archive failed: ${err.message}`); setArchiveConfirmOpen(false); }
      }
    );
  };

  // Deleting someone else's project needs break-glass, so ask for it up front.
  const isNonOwnerAdmin = !!project && isSystemAdmin && !project.myPermissions?.includes('Delete');

  const confirmDeleteProject = (breakGlass?: BreakGlassContext) => {
    deleteProjectContentMutation.mutate({ breakGlass }, {
      onSuccess: () => {
        deleteProjectMutation.mutate({ projectId: projectId!, breakGlass }, {
          onError: (err: any) => showNotification('error', `Archive failed after content delete: ${err.message}`)
        });
      },
      onError: (err: any) => showNotification('error', `Delete failed: ${err.message}`)
    });
  };

  const closeDeleteProjectModal = () => {
    const didDelete = deleteProjectMutation.isSuccess;
    setDeleteProjectFlowOpen(false);
    deleteProjectContentMutation.reset();
    deleteProjectMutation.reset();
    if (didDelete) navigate('/projects');
  };

  const confirmClearProjectContent = (breakGlass?: BreakGlassContext) => {
    deleteProjectContentMutation.mutate({ breakGlass }, {
      onError: (err: any) => showNotification('error', `Clear failed: ${err.message}`)
    });
  };

  const handleDeleteProjectClick = () => {
    if (isNonOwnerAdmin && !grantCtx) {
      setBreakGlassModalTarget('delete');
    } else {
      setDeleteProjectFlowOpen(true);
    }
  };

  // Scheduling a project's deletion needs Project::Delete, so a non-owner admin confirms break-glass first
  const openProjectSchedule = () => {
    if (isNonOwnerAdmin && !grantCtx) {
      setBreakGlassModalTarget('schedule');
    } else {
      setScheduleTarget({ type: 'project', id: projectId!, name: project?.name || projectId!, breakGlass: grantCtx });
    }
  };

  const handleClearContentClick = () => {
    if (isNonOwnerAdmin && !grantCtx) {
      setBreakGlassModalTarget('clear');
    } else {
      setClearProjectContentOpen(true);
    }
  };

  const confirmClearSessionContent = () => {
    deleteSessionContentMutation.mutate(undefined, {
      onError: (err: any) => showNotification('error', `Clear failed: ${err.message}`)
    });
  };

  const userInitials = user?.displayName
    ? user.displayName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)
    : 'U';

  if (isLoading) {
    return (
      <div className="h-screen bg-surface-light flex items-center justify-center text-[#64748b]">
        Loading project...
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="h-screen bg-surface-light flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center max-w-md">
          <p className="text-sm text-red-700">{error ? `Failed to load project: ${error.message}` : 'Project not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-surface-light text-slate-900 flex flex-col font-display overflow-hidden">
      {/* Top bar */}
      <header className="flex items-center justify-between bg-navy px-6 py-3 shrink-0 h-16 shadow-md z-50">
        <div className="flex items-center gap-4 text-white">
          <img src="/spectrum.svg" alt="Spectrum Logo" className="size-8 cursor-pointer" onClick={() => navigate('/projects')} />
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              {renamingProject ? (
                <div className="flex items-center gap-1.5">
                  <input
                    autoFocus
                    value={renameValue}
                    onChange={e => setRenameValue(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { submitRename(); } if (e.key === 'Escape') { setRenamingProject(false); } }}
                    onBlur={submitRename}
                    className="bg-white/10 text-white text-base font-bold leading-tight tracking-wide px-2 py-0.5 rounded border border-white/30 focus:outline-none focus:border-primary w-56"
                  />
                  <button onMouseDown={e => { e.preventDefault(); submitRename(); }} className="text-primary hover:text-primary/80"><Check size={15} /></button>
                  <button onMouseDown={e => { e.preventDefault(); setRenamingProject(false); }} className="text-slate-400 hover:text-white"><X size={15} /></button>
                </div>
              ) : (
                <div className="group flex items-center gap-1.5 cursor-pointer" onClick={startRename}>
                  <h1 className="text-white text-base font-bold leading-tight tracking-wide">{project.name}</h1>
                  <Pencil size={13} className="text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              )}
            </div>
            <nav className="hidden md:flex items-center gap-1.5 text-xs text-slate-400">
              <button onClick={() => navigate('/projects')} className="hover:text-primary transition-colors">
                Projects
              </button>
              <ChevronRight size={10} />
              <span className="text-slate-300">{project.name}</span>
            </nav>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <BreakGlassToggle
            variant="dark"
            scope={isNonOwnerAdmin ? breakGlassScope : null}
            label="Break-glass"
            action={`act on project '${project.name}' that you do not own`}
            note="Ends after 30 minutes or when you leave this project. You can manage access and ownership, delete or schedule deletion, and read; other edits stay off. Everything is audited."
            grantOptions={{ path: `/projects/${projectId}` }}
          />
          {/* Actions menu */}
          <div className="relative">
            <button
              onClick={() => setShowActionsMenu(!showActionsMenu)}
              className="flex items-center justify-center rounded-lg h-9 px-3 bg-white/5 hover:bg-white/10 text-white border border-white/10 transition-colors"
            >
              <MoreVertical size={18} />
            </button>
            {showActionsMenu && (
              <>
                <div onClick={() => setShowActionsMenu(false)} className="fixed inset-0 z-40" />
                <div className="absolute right-0 top-full mt-1 bg-white border border-[#e2e8f0] rounded-xl shadow-lg min-w-[180px] overflow-hidden z-50">
                  {!inBreakGlass && (<>
                  <button
                    onClick={() => { startRename(); setShowActionsMenu(false); }}
                    className="w-full px-4 py-3 flex items-center gap-3 text-sm text-[#052831] hover:bg-slate-50 transition-colors text-left"
                  >
                    <Pencil size={16} /> Rename Project
                  </button>
                  <button
                    onClick={() => { setArchiveConfirmOpen(true); setShowActionsMenu(false); }}
                    className="w-full px-4 py-3 flex items-center gap-3 text-sm text-[#052831] hover:bg-slate-50 transition-colors text-left"
                  >
                    <Archive size={16} /> Archive Project
                  </button>
                  {hasReachFeature && (
                    <button
                      onClick={() => { toggleReachEnabled(); setShowActionsMenu(false); }}
                      className="w-full px-4 py-3 flex items-center gap-3 text-sm text-[#052831] hover:bg-slate-50 transition-colors text-left border-t border-[#e2e8f0]"
                    >
                      <TrendingUp size={16} /> {(project as any)?.reachEnabled ? 'Disable REACH' : 'Enable REACH'}
                    </button>
                  )}
                  {hasBidFeature && (
                    <button
                      onClick={() => { toggleBidEnabled(); setShowActionsMenu(false); }}
                      className="w-full px-4 py-3 flex items-center gap-3 text-sm text-[#052831] hover:bg-slate-50 transition-colors text-left border-t border-[#e2e8f0]"
                    >
                      <FileText size={16} /> {(project as any)?.bidEnabled ? 'Disable Bid Response' : 'Enable Bid Response'}
                    </button>
                  )}
                  {hasTabletopFeature && (
                    <button
                      onClick={() => { toggleTabletopEnabled(); setShowActionsMenu(false); }}
                      className="w-full px-4 py-3 flex items-center gap-3 text-sm text-[#052831] hover:bg-slate-50 transition-colors text-left border-t border-[#e2e8f0]"
                    >
                      <ShieldAlert size={16} /> {(project as any)?.tabletopEnabled ? 'Disable Tabletop Exercise' : 'Enable Tabletop Exercise'}
                    </button>
                  )}
                  </>)}
                  {(project.myPermissions?.includes('ManageAcl') || (isNonOwnerAdmin && grantCtx)) && (
                    <button
                      onClick={() => { activateView('access'); setShowActionsMenu(false); }}
                      className="w-full px-4 py-3 flex items-center gap-3 text-sm text-[#052831] hover:bg-slate-50 transition-colors text-left border-t border-[#e2e8f0]"
                    >
                      <Crown size={16} /> Transfer Ownership
                    </button>
                  )}
                  <button
                    onClick={() => { openProjectSchedule(); setShowActionsMenu(false); }}
                    className="w-full px-4 py-3 flex items-center gap-3 text-sm text-red-500 font-medium hover:bg-red-50 transition-colors border-t border-[#e2e8f0] text-left"
                  >
                    <CalendarClock size={16} /> Schedule Content Deletion
                  </button>
                  <button
                    onClick={() => { handleClearContentClick(); setShowActionsMenu(false); }}
                    className="w-full px-4 py-3 flex items-center gap-3 text-sm text-red-500 font-medium hover:bg-red-50 transition-colors border-t border-[#e2e8f0] text-left"
                  >
                    <Trash2 size={16} /> Clear All Content
                  </button>
                  <button
                    onClick={() => { handleDeleteProjectClick(); setShowActionsMenu(false); }}
                    className="w-full px-4 py-3 flex items-center gap-3 text-sm text-red-500 font-medium hover:bg-red-50 transition-colors border-t border-[#e2e8f0] text-left"
                  >
                    <Trash2 size={16} /> Delete Project
                  </button>
                </div>
              </>
            )}
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center justify-center rounded-lg h-8 w-8 bg-white/5 hover:bg-white/10 text-white/70 hover:text-white border border-white/10 transition-colors"
            title="Sign out"
          >
            <LogOut size={16} />
          </button>
          <button
            onClick={() => openHelp()}
            className="flex items-center justify-center rounded-lg h-8 w-8 bg-white/5 hover:bg-white/10 text-white/70 hover:text-white border border-white/10 transition-colors"
            title="Help"
          >
            <HelpCircle size={16} />
          </button>
          {project.trainingSandboxEnabled && (
            <button
              data-training-id="training-mode-toggle"
              onClick={() => (isTrainingActive ? stopTraining() : startTraining(project.projectId))}
              className={`relative z-[70] flex items-center gap-1.5 rounded-lg h-8 px-3 text-xs font-bold border transition-colors ${
                isTrainingActive
                  ? 'bg-amber-500 border-amber-400 text-white'
                  : 'bg-white/5 hover:bg-white/10 text-white/70 hover:text-white border-white/10'
              }`}
              title={isTrainingActive ? 'Exit training mode' : 'Start training mode'}
            >
              <GraduationCap size={14} /> {isTrainingActive ? 'Training Active' : 'Start Training'}
            </button>
          )}
          <div
            className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-[#052831] font-bold text-xs cursor-pointer hover:bg-primary/80 transition-colors border-2 border-primary/20"
            onClick={() => navigate('/profile')}
            title={user?.displayName || 'Profile'}
          >
            {userInitials}
          </div>
        </div>
      </header>

      {/* Body: sidebar + main */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside className="w-64 bg-sidebar-bg border-r border-slate-200 flex flex-col shrink-0 overflow-y-auto">
          <div className="p-3 flex flex-col gap-6">
            {/* Top nav items */}
            <nav className="flex flex-col gap-1">
              {topItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeView === item.key;
                return (
                  <button
                    key={item.key}
                    onClick={() => activateView(item.key)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all w-full ${
                      isActive
                        ? 'bg-white border border-primary/20 shadow-sm text-primary'
                        : 'text-slate-600 hover:bg-white hover:text-slate-900 hover:shadow-sm'
                    }`}
                  >
                    <Icon size={20} className={isActive ? 'text-primary' : 'text-slate-400'} />
                    <span className={`text-sm ${isActive ? 'font-semibold' : 'font-medium'}`}>{item.label}</span>
                  </button>
                );
              })}
            </nav>

            {/* Prep section */}
            <div className="flex flex-col gap-1">
              <button
                onClick={() => setPrepExpanded(!prepExpanded)}
                className="flex items-center justify-between px-3 mb-1"
              >
                <h2 className="text-[11px] font-bold text-primary tracking-widest uppercase">Prep</h2>
                {prepExpanded ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
              </button>
              {prepExpanded && prepItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeView === item.key;
                return (
                  <button
                    key={item.key}
                    onClick={() => activateView(item.key)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all w-full ${
                      isActive
                        ? 'bg-white border border-primary/20 shadow-sm text-primary'
                        : 'text-slate-600 hover:bg-white hover:text-slate-900 hover:shadow-sm'
                    }`}
                  >
                    <Icon size={20} className={isActive ? 'text-primary' : 'text-slate-400'} />
                    <span className={`text-sm ${isActive ? 'font-semibold' : 'font-medium'}`}>{item.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Ideate section */}
            <div className="flex flex-col gap-1">
              <button
                onClick={() => setIdeateExpanded(!ideateExpanded)}
                className="flex items-center justify-between px-3 mb-1"
              >
                <h2 className="text-[11px] font-bold text-primary tracking-widest uppercase">Ideate</h2>
                {ideateExpanded ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
              </button>
              {ideateExpanded && (
                ideateItems.length > 0 ? ideateItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeView === item.key;
                  return (
                    <button
                      key={item.key}
                      onClick={() => activateView(item.key)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all w-full ${
                        isActive
                          ? 'bg-white border border-primary/20 shadow-sm text-primary'
                          : 'text-slate-600 hover:bg-white hover:text-slate-900 hover:shadow-sm'
                      }`}
                    >
                      <Icon size={20} className={isActive ? 'text-primary' : 'text-slate-400'} />
                      <span className={`text-sm ${isActive ? 'font-semibold' : 'font-medium'}`}>{item.label}</span>
                    </button>
                  );
                }) : (
                  <p className="text-xs text-slate-400 px-3 italic">Coming soon</p>
                )
              )}
            </div>

            {/* Sessions section */}
            <div className="flex flex-col gap-3">
              <button
                onClick={() => setSessionsExpanded(!sessionsExpanded)}
                className="flex items-center justify-between px-3"
              >
                <h2 className="text-[11px] font-bold text-primary tracking-widest uppercase">Sessions</h2>
                {sessionsExpanded ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
              </button>

              {sessionsExpanded && (
                <div className="flex flex-col gap-1">
                  {sessions && sessions.length > 0 ? (
                    sessions.map((session: any) => {
                      const sid = session.sessionId || session.id;
                      return (
                        <div key={sid} className="relative flex items-center group rounded-xl hover:bg-white hover:shadow-sm transition-all">
                          {renamingSessionId === sid ? (
                            <div className="flex items-center gap-1.5 px-3 py-1.5 flex-1 min-w-0">
                              <span className={`w-2 h-2 rounded-full shrink-0 ${getSessionStatusDotClass(session)}`} />
                              <input
                                autoFocus
                                value={renameSessionValue}
                                onChange={e => setRenameSessionValue(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') submitSessionRename(sid, session.customerName || session.name || sid);
                                  if (e.key === 'Escape') setRenamingSessionId(null);
                                }}
                                onBlur={() => submitSessionRename(sid, session.customerName || session.name || sid)}
                                className="flex-1 min-w-0 bg-slate-100 text-slate-700 text-sm px-1.5 py-0.5 rounded border border-slate-300 focus:outline-none focus:border-primary"
                              />
                              <button onMouseDown={e => { e.preventDefault(); submitSessionRename(sid, session.customerName || session.name || sid); }} className="text-primary hover:text-primary/80"><Check size={13} /></button>
                              <button onMouseDown={e => { e.preventDefault(); setRenamingSessionId(null); }} className="text-slate-400 hover:text-slate-600"><X size={13} /></button>
                            </div>
                          ) : (
                            <button
                              onClick={() => openSession(sid)}
                              className="flex items-center gap-3 px-3 py-1.5 text-left flex-1 min-w-0"
                            >
                              <span className={`w-2 h-2 rounded-full shrink-0 ${getSessionStatusDotClass(session)}`} />
                              <span className="text-sm text-slate-600 group-hover:text-primary transition-all truncate">
                                {session.customerName || session.name || sid}
                              </span>
                              <SessionDeletionBadge schedule={deletionSchedules?.sessions[sid]} />
                            </button>
                          )}
                          {renamingSessionId !== sid && (
                            <div className="relative shrink-0 pr-1">
                              <button
                                onClick={(e) => { e.stopPropagation(); setSessionMenuOpenId(sessionMenuOpenId === sid ? null : sid); }}
                                className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-slate-100 transition-all"
                              >
                                <MoreVertical size={14} className="text-slate-400" />
                              </button>
                              {sessionMenuOpenId === sid && (
                                <>
                                  <div onClick={() => setSessionMenuOpenId(null)} className="fixed inset-0 z-40" />
                                  <div className="absolute right-0 top-full mt-1 bg-white border border-[#e2e8f0] rounded-xl shadow-lg min-w-[160px] overflow-hidden z-50">
                                    <button
                                      onClick={() => startSessionRename(sid, session.customerName || session.name || sid)}
                                      className="w-full px-4 py-2.5 flex items-center gap-3 text-sm text-[#052831] hover:bg-slate-50 transition-colors text-left"
                                    >
                                      <Pencil size={13} /> Rename
                                    </button>
                                    <button
                                      onClick={() => { setAuditSessionId(sid); setSessionMenuOpenId(null); }}
                                      className="w-full px-4 py-2.5 flex items-center gap-3 text-sm text-slate-600 hover:bg-slate-50 transition-colors border-t border-[#e2e8f0] text-left"
                                    >
                                      <ShieldCheck size={13} /> Audit data
                                    </button>
                                    {deletionSchedules?.sessionCertificates[sid] && (
                                      <button
                                        onClick={() => { downloadCertificate('session', sid); setSessionMenuOpenId(null); }}
                                        className="w-full px-4 py-2.5 flex items-center gap-3 text-sm text-slate-600 hover:bg-slate-50 transition-colors border-t border-[#e2e8f0] text-left"
                                      >
                                        <Download size={13} /> Deletion certificate
                                      </button>
                                    )}
                                    <button
                                      onClick={() => { setScheduleTarget({ type: 'session', id: sid, name: session.customerName || session.name || sid }); setSessionMenuOpenId(null); }}
                                      className="w-full px-4 py-2.5 flex items-center gap-3 text-sm text-red-500 hover:bg-red-50 transition-colors border-t border-[#e2e8f0] text-left"
                                    >
                                      <CalendarClock size={13} /> Schedule deletion
                                    </button>
                                    <button
                                      onClick={() => { setClearSessionContentId(sid); setSessionMenuOpenId(null); }}
                                      className="w-full px-4 py-2.5 flex items-center gap-3 text-sm text-red-500 font-medium hover:bg-red-50 transition-colors border-t border-[#e2e8f0] text-left"
                                    >
                                      <Trash2 size={13} /> Clear content
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-xs text-slate-400 px-3">No sessions linked</p>
                  )}
                  <button
                    onClick={() => activateView('new-session')}
                    className="flex items-center gap-2 px-3 py-2 text-primary hover:text-primary/80 transition-colors text-sm font-semibold mt-1"
                  >
                    <Plus size={14} />
                    New Session
                  </button>
                </div>
              )}
            </div>

            {/* REACH section — only when org feature + project reachEnabled */}
            {hasReach && (
              <div className="flex flex-col gap-1">
                <button
                  onClick={() => navigate(`/projects/${projectId}/reach`)}
                  className="flex items-center justify-between px-3 mb-1"
                >
                  <h2 className="text-[11px] font-bold text-primary tracking-widest uppercase">Change Management</h2>
                </button>
                {[
                  { label: 'REACH Home', icon: LayoutDashboard, path: `/projects/${projectId}/reach` },
                  { label: 'Stakeholder Map', icon: Map, path: `/projects/${projectId}/reach/stakeholder-map` },
                  { label: 'Assessments', icon: ClipboardCheck, path: `/projects/${projectId}/reach/assessments` },
                  { label: 'Results', icon: BarChart2, path: `/projects/${projectId}/reach/results` },
                  { label: 'Workbook', icon: BookOpen, path: `/projects/${projectId}/reach/workbook` },
                  { label: 'Comms Log', icon: MessageSquare, path: `/projects/${projectId}/reach/comms` },
                  { label: 'Blockers', icon: ShieldAlert, path: `/projects/${projectId}/reach/blockers` },
                  { label: 'Adoption Plan', icon: FileText, path: `/projects/${projectId}/reach/plan` },
                  { label: 'Close-Out', icon: Flag, path: `/projects/${projectId}/reach/closeout` },
                ].map((item) => {
                  const Icon = item.icon;
                  const isActive = pathname === item.path;
                  return (
                    <button
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all w-full ${
                        isActive
                          ? 'bg-white border border-primary/20 shadow-sm text-primary'
                          : 'text-slate-600 hover:bg-white hover:text-slate-900 hover:shadow-sm'
                      }`}
                    >
                      <Icon size={20} className={isActive ? 'text-primary' : 'text-slate-400'} />
                      <span className={`text-sm ${isActive ? 'font-semibold' : 'font-medium'}`}>{item.label}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Bid Response section — only when org feature + project bidEnabled */}
            {hasBid && (
              <div className="flex flex-col gap-1">
                <button
                  onClick={() => navigate(`/projects/${projectId}/bid`)}
                  className="flex items-center justify-between px-3 mb-1"
                >
                  <h2 className="text-[11px] font-bold text-primary tracking-widest uppercase">Bid Response</h2>
                </button>
                {[{ label: 'Bid Home', icon: FileText, path: `/projects/${projectId}/bid` }].map((item) => {
                  const Icon = item.icon;
                  const isActive = pathname === item.path;
                  return (
                    <button
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all w-full ${
                        isActive
                          ? 'bg-white border border-primary/20 shadow-sm text-primary'
                          : 'text-slate-600 hover:bg-white hover:text-slate-900 hover:shadow-sm'
                      }`}
                    >
                      <Icon size={20} className={isActive ? 'text-primary' : 'text-slate-400'} />
                      <span className={`text-sm ${isActive ? 'font-semibold' : 'font-medium'}`}>{item.label}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Tabletop section — only when org feature + project tabletopEnabled */}
            {hasTabletop && (
              <div className="flex flex-col gap-1">
                <button
                  onClick={() => navigate(`/projects/${projectId}/tabletop`)}
                  className="flex items-center justify-between px-3 mb-1"
                >
                  <h2 className="text-[11px] font-bold text-primary tracking-widest uppercase">Tabletop Exercise</h2>
                </button>
                {[{ label: 'Exercises', icon: ShieldAlert, path: `/projects/${projectId}/tabletop` }].map((item) => {
                  const Icon = item.icon;
                  const isActive = pathname === item.path;
                  return (
                    <button
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all w-full ${
                        isActive
                          ? 'bg-white border border-primary/20 shadow-sm text-primary'
                          : 'text-slate-600 hover:bg-white hover:text-slate-900 hover:shadow-sm'
                      }`}
                    >
                      <Icon size={20} className={isActive ? 'text-primary' : 'text-slate-400'} />
                      <span className={`text-sm ${isActive ? 'font-semibold' : 'font-medium'}`}>{item.label}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Delivery section */}
            <div className="flex flex-col gap-1">
              <button
                onClick={() => setDeliveryExpanded(!deliveryExpanded)}
                className="flex items-center justify-between px-3 mb-1"
              >
                <h2 className="text-[11px] font-bold text-primary tracking-widest uppercase">Delivery</h2>
                {deliveryExpanded ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
              </button>
              {deliveryExpanded && [...deliveryItems, ...(hasMcpServer ? [mcpServerItem] : []), ...(hasDemoTemplates ? [demoTemplatesItem] : []), ...(isTrainingSandbox ? [trainingReviewItem] : [])].map((item) => {
                const Icon = item.icon;
                const isActive = activeView === item.key;
                return (
                  <button
                    key={item.key}
                    onClick={() => activateView(item.key)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all w-full ${
                      isActive
                        ? 'bg-white border border-primary/20 shadow-sm text-primary'
                        : 'text-slate-600 hover:bg-white hover:text-slate-900 hover:shadow-sm'
                    }`}
                  >
                    <Icon size={20} className={isActive ? 'text-primary' : 'text-slate-400'} />
                    <span className={`text-sm ${isActive ? 'font-semibold' : 'font-medium'}`}>{item.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Manage section */}
            <div className="flex flex-col gap-1">
              <button
                onClick={() => setManageExpanded(!manageExpanded)}
                className="flex items-center justify-between px-3 mb-1"
              >
                <h2 className="text-[11px] font-bold text-primary tracking-widest uppercase">Manage</h2>
                {manageExpanded ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
              </button>
              {manageExpanded && [...manageItems, ...(hasSessionHub ? [sessionHubItem] : [])].map((item) => {
                const Icon = item.icon;
                const isActive = activeView === item.key;
                return (
                  <button
                    key={item.key}
                    onClick={() => activateView(item.key)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all w-full ${
                      isActive
                        ? 'bg-white border border-primary/20 shadow-sm text-primary'
                        : 'text-slate-600 hover:bg-white hover:text-slate-900 hover:shadow-sm'
                    }`}
                  >
                    <Icon size={20} className={isActive ? 'text-primary' : 'text-slate-400'} />
                    <span className={`text-sm ${isActive ? 'font-semibold' : 'font-medium'}`}>{item.label}</span>
                  </button>
                );
              })}
            </div>

          </div>
        </aside>

        {/* Main content */}
        {outlet ? (
          <main className="flex-1 overflow-hidden flex flex-col">
            <Outlet />
          </main>
        ) : (
          <main className="flex-1 overflow-y-auto p-8">
            <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-8">
              {projectDeletion && (
                <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 rounded-xl border border-red-200 bg-red-50 text-sm text-red-700">
                  <span className="flex items-center gap-2">
                    <CalendarClock size={16} />
                    All content in this project, including every session, will be deleted on {formatDeleteOn(projectDeletion.deleteOn)}
                    {' '}({daysUntilDeleteOn(projectDeletion.deleteOn)} days).
                  </span>
                  <button
                    onClick={openProjectSchedule}
                    className="font-semibold underline hover:text-red-800"
                  >
                    Manage
                  </button>
                </div>
              )}
              {!projectDeletion && projectCertificate && (
                <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 rounded-xl border border-green-200 bg-green-50 text-sm text-green-800">
                  <span className="flex items-center gap-2">
                    <ShieldCheck size={16} />
                    Content deleted on {new Date(projectCertificate.completedAt).toLocaleDateString('en-GB', { dateStyle: 'long' })}
                    {' '}(scheduled for {formatDeleteOn(projectCertificate.deleteOn)}).
                  </span>
                  <button
                    onClick={() => downloadCertificate('project', projectId!)}
                    className="flex items-center gap-2 rounded-xl h-9 px-4 border border-green-200 bg-white text-green-700 text-sm font-bold hover:bg-green-100 shadow-sm transition-colors"
                  >
                    <Download size={16} /> Download certificate
                  </button>
                </div>
              )}
              {/* Tab content header */}
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h2 className="text-3xl font-bold text-[#052831]">
                    {activeView === 'new-session' ? 'New Session' : sidebarItems.find(i => i.key === activeView)?.label}
                  </h2>
                  <p className="text-[#64748b] text-sm mt-1">
                    {activeView === 'dashboard' && `Overview of ${project.name}`}
                    {activeView === 'sessions' && 'Every session in this project: stage, room, attendees, shared items and deletion date.'}
                    {activeView === 'requirements' && 'Manage personas and requirements extracted from live sessions.'}
                    {activeView === 'milestones' && 'Delivery phases. Tag user stories and features to a phase to express what ships when.'}
                    {activeView === 'stakeholders' && 'Track sponsors, sign-off authorities, and consulted/informed parties for this engagement.'}
                    {activeView === 'engagement' && 'Configure your engagement, generate a structured plan with workshops, agendas, and calendar.'}
                    {activeView === 'research' && 'Deep research on customer organisations — search public data, build knowledge graphs.'}
                    {activeView === 'project-graph' && 'Interactive knowledge graph combining research data with session contributions, user stories, and personas.'}
                    {activeView === 'avatars' && 'Manage AI avatar definitions shared across all sessions in this project.'}
                    {activeView === 'ideation' && 'Collect and prioritise AI use case ideas from stakeholders.'}
                    {activeView === 'mcp-server' && 'Connect Claude Code to this project via the Model Context Protocol.'}
                    {activeView === 'session-hub' && 'Set up a private, time-limited client page: who can sign in, for how long, and the link.'}
                    {activeView === 'demo-templates' && 'Browse the demonstrator template catalog and download a starter repo.'}
                    {activeView === 'tasks' && 'Multi-agent async tasks running across sessions.'}
                    {activeView === 'build' && 'Generate a scaffold bundle with CLAUDE.md and specs for Claude Code.'}
                    {activeView === 'knowledge-base' && 'Manage and organize your project reference documents.'}
                    {activeView === 'outputs' && 'View generated outputs from workflows and sessions.'}
                    {activeView === 'file-browser' && 'Browse project files and uploaded assets.'}
                    {activeView === 'costs' && 'Track LLM execution costs across sessions and workflows.'}
                    {activeView === 'access' && 'Manage team access and permissions for this project.'}
                    {activeView === 'new-session' && 'Configure and launch a new co-creation session for this project.'}
                  </p>
                </div>
              </div>

              {/* Tab content */}
              {activeView === 'dashboard' && (
                <OverviewTab project={project} onViewChange={(view) => activateView(view as SidebarView)} />
              )}
              {activeView === 'sessions' && (
                <SessionsTab projectId={projectId!} canManage={hasSessionHub} onOpenSession={openSession} deletionSchedules={deletionSchedules?.sessions} />
              )}
              {activeView === 'requirements' && (
                <RequirementsTab projectId={projectId!} token={accessToken || ''} />
              )}
              {activeView === 'milestones' && (
                <MilestonesTab projectId={projectId!} token={accessToken || ''} />
              )}
              {activeView === 'stakeholders' && (
                <StakeholdersTab projectId={projectId!} token={accessToken || ''} />
              )}
              {activeView === 'engagement' && (
                <EngagementBuilderTab projectId={projectId!} token={accessToken || ''} />
              )}
              {activeView === 'research' && (
                <CustomerResearchTab projectId={projectId!} token={accessToken || ''} />
              )}
              {activeView === 'project-graph' && (
                <ProjectGraphTab projectId={projectId!} token={accessToken || ''} />
              )}
              {activeView === 'avatars' && (
                <ProjectAvatarsTab projectId={projectId!} token={accessToken || ''} />
              )}
              {activeView === 'ideation' && (
                <IdeationTab projectId={projectId!} token={accessToken || ''} />
              )}
              {activeView === 'tasks' && (
                <ProjectTasksTab projectId={projectId!} token={accessToken || ''} />
              )}
              {activeView === 'knowledge-base' && (
                <KnowledgeBaseTab projectId={projectId!} token={accessToken || ''} />
              )}
              {activeView === 'outputs' && (
                <OutputsTab projectId={projectId!} token={accessToken || ''} />
              )}
              {activeView === 'file-browser' && (
                <S3BrowserTab projectId={projectId!} token={accessToken || ''} canWrite={!!project.myPermissions?.includes('Write')} />
              )}
              {activeView === 'costs' && (
                <CostsTab projectId={projectId!} token={accessToken || ''} />
              )}
              {activeView === 'access' && (
                <AccessManagementTab
                  projectId={projectId!}
                  projectOwnerId={project.ownerId}
                  canManageAcl={!!project.myPermissions?.includes('ManageAcl') || (isNonOwnerAdmin && !!grantCtx)}
                  breakGlass={isNonOwnerAdmin ? grantCtx : undefined}
                />
              )}
              {activeView === 'mcp-server' && (
                <McpServerTab projectId={projectId!} token={accessToken || ''} />
              )}
              {activeView === 'session-hub' && hasSessionHub && (
                <SessionHubTab projectId={projectId!} token={accessToken || ''} />
              )}
              {activeView === 'demo-templates' && (
                <DemoTemplatesTab />
              )}
              {activeView === 'training-review' && isTrainingSandbox && (
                <TrainingReviewTab projectId={projectId!} canSignOff={canSignOffTraining} />
              )}
              {activeView === 'build' && (
                <BuildWithAI projectId={projectId} embedded />
              )}
              {activeView === 'new-session' && (
                <CreateSessionTab projectId={projectId!} />
              )}
            </div>
          </main>
        )}

        <TrainingGuidePanel />
      </div>

      {/* Notification */}
      {notification && (
        <div className={`fixed top-16 right-5 px-4 py-3 rounded-xl text-sm font-medium shadow-lg z-[1000] ${
          notification.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {notification.message}
        </div>
      )}

      {/* Confirm Modals */}
      <ConfirmModal
        isOpen={archiveConfirmOpen}
        onClose={() => setArchiveConfirmOpen(false)}
        onConfirm={confirmArchive}
        title="Archive Project"
        message="Are you sure you want to archive this project? Archiving does not delete workshop data, files, or AI indexes. Use Delete Content on the project to permanently remove customer data."
        confirmText="Archive"
        cancelText="Cancel"
        variant="warning"
        isProcessing={updateProjectMutation.isPending}
      />
      <DeletionProgressModal
        isOpen={deleteProjectFlowOpen}
        onClose={closeDeleteProjectModal}
        onConfirm={() => confirmDeleteProject(grantCtx)}
        title="Delete project"
        message="Permanently deletes all sessions, transcripts, personas, device images, knowledge base documents, S3 files, graph nodes, AI vectors, and audit events. The project is then removed."
        isProcessing={deleteProjectContentMutation.isPending || deleteProjectMutation.isPending}
        result={deleteProjectContentMutation.data ?? null}
        error={deleteProjectContentMutation.error?.message ?? deleteProjectMutation.error?.message ?? null}
      />
      <DeletionProgressModal
        isOpen={clearProjectContentOpen}
        onClose={() => { setClearProjectContentOpen(false); deleteProjectContentMutation.reset(); }}
        onConfirm={() => confirmClearProjectContent(grantCtx)}
        title="Delete all project content"
        message="Permanently deletes all sessions, transcripts, personas, device images, knowledge base documents, S3 files, graph nodes, AI vectors, and audit events for this project. The project record is preserved."
        isProcessing={deleteProjectContentMutation.isPending}
        result={deleteProjectContentMutation.data ?? null}
        error={deleteProjectContentMutation.error?.message ?? null}
      />
      <BreakGlassModal
        isOpen={!!breakGlassModalTarget}
        onClose={() => setBreakGlassModalTarget(null)}
        onConfirm={(ctx) => {
          const target = breakGlassModalTarget;
          setBreakGlassModalTarget(null);
          if (target === 'delete') {
            setDeleteProjectFlowOpen(true);
            confirmDeleteProject(ctx);
          } else if (target === 'clear') {
            setClearProjectContentOpen(true);
            confirmClearProjectContent(ctx);
          } else if (target === 'schedule') {
            setScheduleTarget({ type: 'project', id: projectId!, name: project?.name || projectId!, breakGlass: ctx });
          }
        }}
        action={
          breakGlassModalTarget === 'delete'
            ? `delete project '${project?.name}'`
            : breakGlassModalTarget === 'schedule'
              ? `schedule content deletion for project '${project?.name}'`
              : `clear all content for project '${project?.name}'`
        }
      />
      <DeletionProgressModal
        isOpen={!!clearSessionContentId}
        onClose={() => { setClearSessionContentId(null); deleteSessionContentMutation.reset(); }}
        onConfirm={confirmClearSessionContent}
        title="Delete session content"
        message="Permanently deletes all transcripts, personas, contributions, device images, graph nodes, AI vectors, and audit events for this session. This cannot be undone."
        isProcessing={deleteSessionContentMutation.isPending}
        result={deleteSessionContentMutation.data ?? null}
        error={deleteSessionContentMutation.error?.message ?? null}
        sessionId={clearSessionContentId ?? undefined}
      />
      {scheduleTarget && (
        <ScheduleDeletionModal
          isOpen
          onClose={() => setScheduleTarget(null)}
          targetType={scheduleTarget.type}
          targetId={scheduleTarget.id}
          targetName={scheduleTarget.name}
          breakGlass={scheduleTarget.breakGlass}
        />
      )}
      <DeletionProgressModal
        isOpen={!!auditSessionId}
        onClose={() => setAuditSessionId(null)}
        onConfirm={() => {}}
        title="Session data audit"
        message=""
        isProcessing={false}
        sessionId={auditSessionId ?? undefined}
        auditOnly
      />
    </div>
  );
};

/** Small date chip on a session row when its content deletion is scheduled */
const SessionDeletionBadge: React.FC<{ schedule?: DeletionSchedule }> = ({ schedule }) => {
  if (!schedule) return null;
  return (
    <span
      title={`Content deletion scheduled for ${formatDeleteOn(schedule.deleteOn)}`}
      className="shrink-0 flex items-center gap-1 text-[10px] font-semibold text-red-600 bg-red-50 border border-red-200 rounded-full px-1.5 py-0.5"
    >
      <CalendarClock size={10} /> {schedule.deleteOn.slice(5)}
    </span>
  );
};

export default ProjectDetail;
